import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputLabel,
  CircularProgress,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "react-query";
import {
  getChoiceQuestions,
  getAutoNotificationTemplates,
} from "../../../../services/choices/choices_services";
import { getServiceCategories } from "../../../../services/services/services";
import { updateSurveyEvent } from "../../../../services/choices/choices_services";
import { queryClient } from "../../../../providers/query/query";
import { MODULE_CONFIG } from "./module_config";

interface QuestionModalProps {
  open: boolean;
  onClose: () => void;
  choiceId: number;
  mode?: "view" | "edit" | "create";
  initialData?: any;
  onSubmit?: (payload: any) => void;
}
const MODULE_TYPES = [
  "SCHEME",
  "JOB",
  "SURVEY",
  "NEWS",
  "HOSPITAL",
  "SERVICES",
  "NEEDS",
  "EXTERNAL",
  "CMS",
  "PROFILE",
  "HOMEPAGE",
  "REFERRAL",
  "SERVICE_CATEGORY",
  "SERVICE_SUBCATEGORY",
  "SERVICE_LISTING",
  "JOB_LISTING",
  "NEED_LISTING",
];

const QuestionEditAndViewModal = ({
  open,
  onClose,
  choiceId,
  mode = "create",
  initialData
}: QuestionModalProps) => {
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [selectedModule, setSelectedModule] = useState<any>("");

  const [selectedAnswer, setSelectedAnswer] = useState<any>("");
  const [selectedCategoryId, setSelectedCategoryId] =
    useState<any>("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] =
    useState<any>("");
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<any>("");

  // QUESTIONS
  const { data: choiceQuestions, isLoading } = useQuery<any>(
    ["choice-question", choiceId],
    async () => {
      const response = await getChoiceQuestions(+choiceId);
      return response?.data?.data || [];
    },
    {
      enabled: !!choiceId,
    }
  );

  // TEMPLATES
  const { data: templates } = useQuery<any>(
    ["auto-notification-templates"],
    async () => {
      const response = await getAutoNotificationTemplates();
      return response?.data?.data || [];
    }
  );

  // CATEGORIES
  const { data: serviceCategories } = useQuery<any>(
    ["needs-filters"],
    async () => {
      const data = await getServiceCategories();
      return data?.data?.data?.list || [];
    }
  );

  const { mutate: updateEvent } = useMutation(updateSurveyEvent, {
    onSuccess: (data) => {
      setSubmitLoading(false);
      onClose();
      queryClient.invalidateQueries("autoNotificationCreatedEvent");
    }
  })

  // FLATTEN QUESTIONS
  const allQuestions = useMemo(() => {
    return (
      choiceQuestions?.details?.pages
        ?.flatMap((page: any) => page?.elements || [])
        ?.filter(
          (q: any) =>
            q.type === "dropdown" || q.type === "checkbox"
        ) || []
    );
  }, [choiceQuestions]);

  const currentModuleConfig =
    MODULE_CONFIG[selectedModule] || {
      hasModule: false,
      hasSubModule: false,
    };

  // PREFILL EDIT / VIEW
  useEffect(() => {
    if (
      (mode === "edit" || mode === "view") &&
      initialData &&
      allQuestions.length > 0
    ) {
      const matchedQuestion = allQuestions.find(
        (q: any) =>
          q.name === initialData?.question_name
      );

      setSelectedModule(initialData?.module || "");
      setSelectedCategoryId(initialData?.module_id || "");
      setSelectedSubCategoryId(initialData?.submodule_id || "");
      setSelectedQuestion(matchedQuestion || null);
      setSelectedAnswer(initialData?.answer || "");
      setSelectedTemplateId(
        initialData?.template_id || ""
      );
    }
  }, [initialData, allQuestions, mode]);

  const getSubCategories = () => {
    return (
      serviceCategories?.find(
        (c: any) => c.id == selectedCategoryId
      )?.subcategories || []
    );
  };

  const handleSubmit = () => {
    const payload: any = {
      choiceId,
      questionName: selectedQuestion?.name || "",
      questionTitle: selectedQuestion?.title || "",
      answer: selectedAnswer,
      templateId: selectedTemplateId || 0,
      module: selectedModule || null,
      id: initialData?.id || null,
    };

    if (selectedCategoryId) {
      payload.moduleId = selectedCategoryId;
    }

    if (selectedSubCategoryId) {
      payload.submoduleId = selectedSubCategoryId;
    }

    setSubmitLoading(true);
    updateEvent(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      {/* HEADER */}
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={700}>
            {isView
              ? "View Event"
              : isEdit
                ? "Edit Event"
                : "Create Event"}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent dividers>
        {isLoading && !selectedQuestion ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="40vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>

            {/* DETAIL VIEW */}
            {selectedQuestion && (
              <Box>
                {/* <Typography
                  fontWeight={700}
                  mb={3}
                >
                  {selectedQuestion.title}
                </Typography> */}

                {/* ANSWER */}
                {selectedQuestion.type ===
                  "dropdown" && (
                    <FormControl
                      fullWidth
                      sx={{ mb: 3 }}
                      disabled={isView}
                    >
                      <InputLabel>
                        Select Answer
                      </InputLabel>

                      <Select
                        value={selectedAnswer}
                        label="Select Answer"
                        onChange={(e) =>
                          setSelectedAnswer(
                            e.target.value
                          )
                        }
                      >
                        {selectedQuestion?.choices?.map(
                          (
                            opt: any,
                            i: number
                          ) => (
                            <MenuItem key={i} value={typeof opt === 'object' ? opt?.value : opt}>
                              {typeof opt === 'object' ? opt?.value : opt}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}

                {selectedQuestion.type ===
                  "checkbox" && (
                    <Box mb={3}>
                      {selectedQuestion?.choices?.map(
                        (
                          opt: any,
                          i: number
                        ) => (
                          <FormControlLabel
                            key={i}
                            disabled={isView}
                            control={
                              <Checkbox
                                checked={
                                  selectedAnswer ===
                                  (typeof opt === "object" ? opt?.value : opt)
                                }
                                onChange={() =>
                                  setSelectedAnswer(
                                    typeof opt === 'object' ? opt?.value : opt
                                  )
                                }
                              />
                            }
                            label={typeof opt === 'object' ? opt?.value : opt}
                          />
                          )
                      )}
                    </Box>
                  )}

                <FormControl fullWidth sx={{ mb: 3 }} disabled={isView}>
                  <InputLabel>Module Type</InputLabel>

                  <Select
                    value={selectedModule}
                    label="Module Type"
                    onChange={(e) => {
                      setSelectedModule(e.target.value);
                      setSelectedCategoryId("");
                      setSelectedSubCategoryId("");
                    }}
                  >
                    {MODULE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* CATEGORY */}
                {currentModuleConfig.hasModule && (
                  <TextField
                    fullWidth
                    sx={{ mb: 3 }}
                    label={currentModuleConfig.moduleLabel}
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    disabled={isView}
                  />
                )}

                {/* SUBCATEGORY */}
                {currentModuleConfig.hasSubModule && (
                  <TextField
                    fullWidth
                    sx={{ mb: 3 }}
                    label={currentModuleConfig.subModuleLabel}
                    value={selectedSubCategoryId}
                    onChange={(e) =>
                      setSelectedSubCategoryId(e.target.value)
                    }
                    disabled={isView}
                  />
                )}

                {/* TEMPLATE */}
                <FormControl
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={isView}
                >
                  <InputLabel>
                    Template
                  </InputLabel>

                  <Select
                    value={selectedTemplateId}
                    label="Template"
                    onChange={(e) =>
                      setSelectedTemplateId(
                        e.target.value
                      )
                    }
                  >
                    {templates?.map(
                      (template: any) => (
                        <MenuItem
                          key={template.id}
                          value={template.id}
                        >
                          {template.title}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* FOOTER */}
      {!isView && selectedQuestion && (
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
              />
            ) : isEdit ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QuestionEditAndViewModal;